import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { ShoppingCart, Star, SlidersHorizontal, Search, ShieldCheck } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { Link } from 'react-router-dom';
import CardWishlistButton from '../../components/CardWishlistButton'; // Adjust path as needed

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'products'));
        const productList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProducts(productList);

        const uniqueCategories = ['All', ...new Set(productList.map(p => p.category).filter(Boolean))];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error("Error fetching shop products: ", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  let filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
    const matchesSearch = product.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  if (sortBy === 'low-high') {
    filteredProducts.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
  } else if (sortBy === 'high-low') {
    filteredProducts.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-emerald-900 to-ecoGreen-dark text-white py-10 px-4 sm:px-6 lg:px-8 shadow-md">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div>
            <span className="bg-emerald-700/60 text-emerald-200 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
              Prime Green Store
            </span>
            <h1 className="text-3xl font-extrabold tracking-tight mt-2">EcoStore Directory</h1>
            <p className="text-xs sm:text-sm text-emerald-100 mt-1">Explore certified sustainable products with fast, plastic-free delivery.</p>
          </div>
          
          <div className="relative w-full md:w-96">
            <input 
              type="text" 
              placeholder="Search eco-friendly essentials..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white text-gray-800 px-4 py-3 pl-10 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-inner"
            />
            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-3.5" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          {/* Sidebar Categories */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 h-fit space-y-6">
            <div>
              <h3 className="font-extrabold text-gray-900 text-xs mb-3 uppercase tracking-wider flex items-center space-x-2">
                <SlidersHorizontal className="h-4 w-4 text-emerald-700" />
                <span>Categories</span>
              </h3>
              <div className="space-y-1.5">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`w-full text-left px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all ${
                      selectedCategory === category
                        ? 'bg-emerald-800 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <h3 className="font-extrabold text-gray-900 text-xs mb-2 uppercase tracking-wider">Eco-Guarantee</h3>
              <div className="flex items-center space-x-2 text-xs text-gray-600 bg-emerald-50/50 p-3 rounded-xl border border-emerald-100">
                <ShieldCheck className="h-5 w-5 text-emerald-700 flex-shrink-0" />
                <span>100% Verified Sustainable Material</span>
              </div>
            </div>
          </div>

          {/* Product Grid Area */}
          <div className="lg:col-span-3 space-y-4">
            
            {/* Sort Bar */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-200 flex justify-between items-center text-xs sm:text-sm">
              <span className="text-gray-500 font-medium">
                Showing <span className="font-bold text-gray-900">{filteredProducts.length}</span> results
              </span>
              <div className="flex items-center space-x-2">
                <span className="text-gray-400 uppercase font-bold text-[10px]">Sort by:</span>
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-gray-50 border border-gray-300 rounded-xl px-3 py-2 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-800"
                >
                  <option value="featured">Featured</option>
                  <option value="low-high">Price: Low to High</option>
                  <option value="high-low">Price: High to Low</option>
                </select>
              </div>
            </div>

            {/* Product Cards Grid */}
            {loading ? (
              <div className="text-center text-gray-400 py-20 font-medium">Loading store inventory...</div>
            ) : filteredProducts.length === 0 ? (
              <div className="bg-white p-12 rounded-2xl text-center shadow-sm border border-gray-200">
                <p className="text-gray-600 mb-2 font-medium">No products match your search criteria.</p>
                <button 
                  onClick={() => { setSelectedCategory('All'); setSearchQuery(''); }} 
                  className="mt-2 text-emerald-700 font-bold text-xs hover:underline"
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {filteredProducts.map((product) => (
                  <div key={product.id} className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-200 flex flex-col justify-between overflow-hidden group">
                    <div>
                      <Link to={`/product/${product.id}`} className="block relative overflow-hidden bg-gray-100 h-52">
                        {product.image ? (
                          <img 
                            src={product.image} 
                            alt={product.name} 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                          />
                        ) : (
                          <div className="w-full h-full bg-emerald-50 flex items-center justify-center text-emerald-800 font-bold text-sm">
                            🌿 {product.name}
                          </div>
                        )}
                        
                        {/* Wishlist Button Added Here */}
                        <div className="absolute top-3 left-3 z-10">
                          <CardWishlistButton product={product} />
                        </div>

                        <span className="absolute top-3 right-3 bg-white/90 backdrop-blur-md text-[10px] font-bold px-2.5 py-1 rounded-full text-gray-800 shadow-sm uppercase tracking-wider">
                          {product.category || 'Eco'}
                        </span>
                      </Link>
                      
                      <div className="p-4">
                        <div className="flex items-center space-x-1 text-amber-500 text-xs mb-1.5">
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className="h-3 w-3 fill-current" />
                            ))}
                          </div>
                          <span className="text-gray-400 font-semibold ml-1">({product.rating || '4.8'})</span>
                        </div>
                        <Link to={`/product/${product.id}`}>
                          <h3 className="font-bold text-gray-900 text-sm line-clamp-1 hover:text-emerald-700 transition-colors">{product.name}</h3>
                        </Link>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2 leading-relaxed">{product.description || "Zero-waste sustainable alternative for daily use."}</p>
                        
                        <div className="mt-3 flex items-baseline justify-between">
                          <span className="text-lg font-black text-gray-900">₹{product.price}</span>
                          <span className="text-[10px] text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">
                            FREE Delivery
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 pt-0">
                      <button 
                        onClick={() => addToCart(product)}
                        className="w-full bg-emerald-800 hover:bg-emerald-900 text-white py-2.5 rounded-xl font-bold text-xs transition shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
                      >
                        <ShoppingCart className="h-4 w-4" />
                        <span>Add to Cart</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>

        </div>
      </div>
    </div>
  );
}